// Most globals (body bg/text, font stacks, paper grain, dark-mode tokens,
// focus rings, and a11y utilities like .sr-only / .clipped-focus) live in
// `src/index.css` + `src/styles/tokens.css`. This component handles runtime
// concerns only: re-injecting the Google Fonts <link> if the stylesheet was
// stripped (some service-worker caches do this), plus a targeted reset for
// things @import can't cover (tap-highlight, scrollbar styling).

import { useEffect } from 'react';
import { FONT_LOADER_HREF } from './fonts';

const RESET_CSS = `
  * { -webkit-tap-highlight-color: transparent; }
  body { margin: 0; min-height: 100dvh; touch-action: manipulation; overscroll-behavior-y: contain; }
  button, input, select, textarea { font-family: inherit; color: inherit; }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: var(--ap-parchment); }
  ::-webkit-scrollbar-thumb { background: var(--ap-rule); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--ap-muted); }
`;

function ensureFontLink() {
  if (typeof document === 'undefined') return;

  // Bail out if the fonts are already loaded by a <link> stylesheet (e.g. the
  // @import in index.css). Checking for the existing element avoids a duplicate
  // network request; additionally checking document.fonts prevents a double
  // request when index.css has already pulled in the fonts.
  const id = 'gg-font-loader';
  if (document.getElementById(id)) return;

  // If any existing stylesheet href already covers the Google Fonts URL,
  // skip injection — index.css @import has it covered.
  const existingLink = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  ).find((el) => el.href.includes('fonts.googleapis.com'));
  if (existingLink) return;

  // Bail if the display font is already available (service-worker cache hit
  // or CSS @import already resolved).
  if (typeof document.fonts !== 'undefined' && document.fonts.check('1em Fraunces')) return;

  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';
  document.head.appendChild(preconnect1);

  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect2);

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = FONT_LOADER_HREF;
  document.head.appendChild(link);
}

export function GlobalStyles() {
  useEffect(() => {
    ensureFontLink();
  }, []);

  return <style>{RESET_CSS}</style>;
}
